import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Eye, Pencil } from "lucide-react";
import { parseNewDateToDDMMYYYWithoutSeconds } from "../../../../../Utils/dateHelper";

function PriceDefinitionTable({ priceDefinitions, handleEditDefinition, handleViewDefinition, searchValue, ...props }) {
  const classes = useStyles();

  // General
  const [sortedPriceDefinitions, setSortedPriceDefinitions] = useState([]);

  useEffect(() => {
    if (priceDefinitions && priceDefinitions.length > 0) {
      setSortedPriceDefinitions(priceDefinitions);
    } else {
      setSortedPriceDefinitions([]);
    }
  }, [priceDefinitions]);

  const handleSorting = (sortType) => {
    console.log("Sorting by: ", sortType);
  };

  const formatPrice = (priceEntry) => {
    if (priceEntry) {
      return `${priceEntry.currencySymbol}${priceEntry.price
        .toFixed(3)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
        .replace(/\.(\d{3})$/, ",$1")}`;
    } else {
      return "N/A";
    }
  };

  const formatDate = (date) => {
    if (date) {
      let tempDate = new Date(date);
      return parseNewDateToDDMMYYYWithoutSeconds(tempDate);
    } else {
      return "";
    }
  };

  // Function to highlight search text
  const highlightText = (text, searchValue) => {
    if (!searchValue || !text || searchValue.trim() === "") return text;

    const trimmedSearchValue = searchValue.trim();
    const regex = new RegExp(`(${trimmedSearchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === trimmedSearchValue.toLowerCase()) {
        return (
          <mark key={index} style={{ backgroundColor: "var(--color-primary)", color: "#F8F8F8", padding: "0", borderRadius: "2px" }}>
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  return (
    <React.Fragment>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead className={classes.table__header}>
            <tr>
              <th onClick={() => handleSorting("code")}>Code</th>
              <th onClick={() => handleSorting("name")}>Name</th>
              <th onClick={() => handleSorting("description")}>Description</th>
              <th onClick={() => handleSorting("price")} style={{ textAlign: "right" }}>
                Price
              </th>
              <th onClick={() => handleSorting("uom")}>UOM</th>
              <th onClick={() => handleSorting("account")}>Default account</th>
              <th onClick={() => handleSorting("company")}>Company</th>
              <th onClick={() => handleSorting("updatedon")}>Updated on</th>
              <th style={{ minWidth: "16px" }} />
            </tr>
          </thead>
          <tbody className={classes.table__body}>
            {Array.isArray(sortedPriceDefinitions) && sortedPriceDefinitions.length > 0 ? (
              sortedPriceDefinitions.map((definition, index) => (
                <tr className={classes.table__row} key={index}>
                  <td>{highlightText(definition.code, searchValue)}</td>
                  <td>{highlightText(definition.name, searchValue)}</td>
                  <td className={classes.descriptionCell} title={definition.description}>
                    {definition.description}
                  </td>
                  <td style={{ textAlign: "right" }}>{formatPrice(definition.entries.find((entry) => entry.isCurrentPrice))}</td>
                  <td>{definition.entries.find((entry) => entry.isCurrentPrice)?.unitOfMeasurement || "-"}</td>
                  <td>{definition.defaultAccountName || <span style={{ color: "#AAAAAA" }}>—</span>}</td>
                  <td>{definition.companyName}</td>
                  <td>{formatDate(definition.updatedOn)}</td>
                  <td>
                    <div className={classes.buttonContainer}>
                      <button className={classes.iconButton} onClick={() => handleViewDefinition(definition.id)}>
                        <Eye className={classes.iconButton__icon} />
                      </button>
                      <button className={classes.iconButton} onClick={() => handleEditDefinition(definition.id)}>
                        <Pencil className={classes.iconButton__icon} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" style={{ textAlign: "center", padding: "32px" }}>
                  No price definitions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}

export default PriceDefinitionTable;

const useStyles = makeStyles({
  tablesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },

  header: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "16px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "#F8F8F8",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: "0",

    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
  },

  table__header: {
    backgroundColor: "#DFDFDF",
    borderRadius: "8px 8px 0px 0px",

    "& th": {
      height: "36px",
      padding: "0px 16px",
      textAlign: "left",
      fontSize: "16px",
      fontWeight: "normal",
      color: "#0C0E29",

      "&:first-child": {
        borderTopLeftRadius: "8px",
      },

      "&:last-child": {
        borderTopRightRadius: "8px",
        width: "0px",
        padding: "0px",
      },
    },
  },

  table__body: {
    "& tr": {
      borderBottom: "1px solid #EDEDED",

      "&:last-child": {
        borderBottom: "none",

        "& td": {
          "&:first-child": {
            borderBottomLeftRadius: "8px",
          },

          "&:last-child": {
            borderBottomRightRadius: "8px",
          },
        },
      },

      "& td": {
        height: "48px",
        padding: "0px 16px",
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#0C0E29",

        whiteSpace: "nowrap", // Prevent wrapping for all cells
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 200, // Default max width for all cells

        borderRight: "1px solid #EDEDED",

        "&:last-child": {
          borderRight: "none",
          width: "fit-content",
          padding: "8px",
        },
      },
    },
  },

  descriptionCell: {
    maxWidth: 300,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
  },

  table__row: {
    backgroundColor: "#FFFFFF !important",
  },
});
